import { useTheme } from "@mui/material";
import { Tick, TooltipItem, ChartOptions } from "chart.js";
import { format } from "date-fns";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";

type SparkLineProps = {
  data: number[];
  timestamps: string[];
  yMin: number;
};

export const SparkLine = ({ data, timestamps, yMin }: SparkLineProps) => {
  const theme = useTheme();

  // Return nothing if there’s no data
  if (!data || data.length === 0 || !timestamps || timestamps.length === 0) {
    return null;
  }

  // Compute the Y-axis minimum from data and given yMin
  const dataMin = Math.min(...data);
  const computedYMin = yMin !== undefined ? yMin : Math.min(dataMin, 0);

  // Convert each timestamp into a millisecond value
  const xDataPoints = timestamps.map((ts) => new Date(ts).getTime());

  // Determine the fixed x-axis boundaries
  const xMin = Math.min(...xDataPoints);
  const xMax = Math.max(...xDataPoints);

  const tickValues = [
    xMin,
    xMin + (xMax - xMin) * 0.25,
    xMin + (xMax - xMin) * 0.5,
    xMin + (xMax - xMin) * 0.75,
    xMax
  ];

  const chartData = {
    datasets: [
      {
        data: data.map((y, index) => ({ x: xDataPoints[index], y })),
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary.main,
        borderWidth: 1,
        tension: 0.3
      }
    ]
  };

  // Build chart options:
  const chartOptions: ChartOptions<"line"> = {
    maintainAspectRatio: false,
    responsive: true,
    layout: { padding: { right: 24 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        callbacks: {
          title: (tooltipItems: TooltipItem<"line">[]) => {
            const x = tooltipItems[0]?.parsed.x;
            return x != null ? format(new Date(x), "mm:ss") : "";
          },
          label: (tooltipItem: TooltipItem<"line">) =>
            `Value: ${tooltipItem.parsed.y}`
        },
        backgroundColor: theme.palette.background.default,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 8,
        displayColors: false
      }
    },
    scales: {
      x: {
        display: true,
        type: "time",
        min: xMin,
        max: xMax,
        time: {
          unit: "second",
          displayFormats: { second: "hh:mm" }
        },
        grid: {
          display: true,
          color: theme.palette.divider,
          drawTicks: true,
          drawOnChartArea: false
        },
        ticks: {
          autoSkip: false,
          autoSkipPadding: 20,
          minRotation: 45,
          maxRotation: 45
        },
        afterBuildTicks(scale) {
          const newTicks = tickValues.map((val) => ({
            value: val,
            label: format(new Date(val), "hh:mm")
          }));
          scale.ticks.splice(0, scale.ticks.length, ...newTicks);
        },
        border: {
          display: true,
          color: theme.palette.divider
        }
      },
      y: {
        display: true,
        min: computedYMin,
        grace: "5%",
        grid: {
          display: true,
          drawOnChartArea: true,
          drawTicks: true,
          color: theme.palette.divider
        },
        ticks: {
          display: true,
          callback: function (value: string | number, _index, ticks: Tick[]) {
            const first = ticks[0].value;
            const last = ticks[ticks.length - 1].value;
            if (value === first || value === last) {
              return `${Number(value).toFixed(0)}`;
            }
            return "";
          }
        },
        border: {
          display: true,
          color: theme.palette.divider
        }
      }
    },
    elements: {
      line: { tension: 0.4 },
      point: {
        radius: 0.5,
        hoverRadius: 6,
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.divider,
        borderWidth: 1
      }
    },
    animation: false
  };

  return (
    <div
      style={{
        width: 200,
        height: 100,
        display: "flex",
        alignItems: "center"
      }}
    >
      <Line data={chartData} options={chartOptions} />
    </div>
  );
};

export default SparkLine;
